// @ts-check

// Vendor imports
const PropTypes = require('prop-types')
const React = require('react')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')

const context = React.createContext({
  depth: 0,
  decrement: () => {},
  increment: () => {}
})

/**
 * React Component that provides the modal depth context.
 * @param {Object} props
 * @param {React.ElementType} props.children
 */
function ModalDepthManager(props) {
  const [ depth, setDepth ] = React.useState(0)
  const decrement = () => setDepth(prevDepth => prevDepth - 1)
  const increment = () => setDepth(prevDepth => prevDepth + 1)

  return (
    tag(context.Provider, {
      value: {
        depth,
        decrement,
        increment
      }
    },
      props.children
    )
  )
}

ModalDepthManager.propTypes = {
  children: PropTypes.node
}

/**
 * React Component that gets the current modal depth from the context.
 * Use this in class components where `useModalDepth` cannot be used directly.
 * @param {Object} props
 * @param {(depth: number) => React.ElementType} props.children
 */
function ModalDepthConsumer(props) {
  const depth = useModalDepth()
  return props.children(depth)
}

ModalDepthConsumer.propTypes = {
  children: PropTypes.func.isRequired
}

/**
 * React Component that updates the modal depth when mounted/unmounted.
 * Use this in class components where `useModalDepthModifier` cannot be used directly.
 * @returns {React.ElementType}
 */
function ModalDepthModifier() {
  useModalDepthModifier()
  return null
}

/**
 * React Component that prevents scrolling on `body` when modal depth > 0
 * @returns {React.ElementType}
 */
function ModalDepthBodyEffect() {
  useModalDepthBodyEffect()
  return null
}

/**
 * @param {Object} props
 * @param {React.RefObject<HTMLElement>} props.forwardedRef
 * @returns {React.ElementType}
 */
function ModalDepthScrollbarOffsetEffect(props) {
  useModalDepthScrollbarOffsetEffect(props.forwardedRef)
  return null
}

/**
 * React Hook that gets the current modal depth from the context.
 * @returns {number}
 */
function useModalDepth() {
  const { depth } = React.useContext(context)
  return depth
}

/**
 * React Hook that updates the modal depth when mounted/unmounted.
 */
function useModalDepthModifier() {
  const { increment, decrement } = React.useContext(context)
  React.useEffect(
    () => {
      increment()
      return () => {
        decrement()
      }
    },
    []
  )
}

/**
 * React Hook that prevents scrolling on `body` when modal depth > 0
 */
function useModalDepthBodyEffect() {
  if (typeof document === 'object') {
    const isBodyScrollingDisabled = useModalDepth() > 0
    React.useEffect(
      () => {
        if (isBodyScrollingDisabled) {
          const el = document.body
          const scrollbarWidth = window.innerWidth - el.clientWidth
          const borderRight = `${scrollbarWidth}px solid transparent`
          el.style.setProperty('border-right', borderRight)
          el.style.setProperty('overflow', 'hidden')
          return () => {
            el.style.removeProperty('border-right')
            el.style.removeProperty('overflow')
          }
        }
      },
      [isBodyScrollingDisabled]
    )
  }
}

/**
 * @param {React.RefObject<HTMLElement>} domRef
 */
function useModalDepthScrollbarOffsetEffect(domRef) {
  if (typeof document === 'object') {
    const isBodyScrollingDisabled = useModalDepth() > 0
    React.useEffect(
      () => {
        const el = domRef.current
        if (isBodyScrollingDisabled && el) {
          const bodyStyle = document.body.style
          const borderRight = bodyStyle.getPropertyValue('border-right')
          el.style.setProperty('border-right', borderRight)
          return () => {
            el.style.removeProperty('border-right')
          }
        }
      },
      [isBodyScrollingDisabled]
    )
  }
}

module.exports = {
  ModalDepthManager,
  ModalDepthConsumer,
  ModalDepthModifier,
  ModalDepthBodyEffect,
  ModalDepthScrollbarOffsetEffect,
  useModalDepth,
  useModalDepthModifier,
  useModalDepthBodyEffect,
  useModalDepthScrollbarOffsetEffect
}
